import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import {
  Plus, Trash2, Download, Wrench, Euro, TrendingUp, TrendingDown, RefreshCw,
  Zap, Droplets, Flame, Home, Building2, DoorOpen, PaintBucket, ChefHat, Bath, Trees, Shield, Thermometer,
  Upload, Camera, FileText as FileIcon, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadBlob } from '../utils/download';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const CATEGORIES = [
  'Electricite', 'Plomberie', 'Chauffage / Climatisation', 'Toiture / Charpente',
  'Gros oeuvre / Structure', 'Isolation / Energetique', 'Menuiseries interieures',
  'Menuiseries exterieures', 'Revetements interieurs', 'Cuisine', 'Salle de bain / WC',
  'Exterieur / Facade', 'Conformite / Diagnostics'
];

const CAT_ICONS = {
  'Electricite': Zap, 'Plomberie': Droplets, 'Chauffage / Climatisation': Flame,
  'Toiture / Charpente': Home, 'Gros oeuvre / Structure': Building2,
  'Isolation / Energetique': Thermometer, 'Menuiseries interieures': DoorOpen,
  'Menuiseries exterieures': DoorOpen, 'Revetements interieurs': PaintBucket,
  'Cuisine': ChefHat, 'Salle de bain / WC': Bath, 'Exterieur / Facade': Trees,
  'Conformite / Diagnostics': Shield,
};

const formatCurrency = (v) => (v || 0).toLocaleString('fr-FR') + ' EUR';

const TravauxForm = ({ travaux, projects, artisans, onSave, onClose }) => {
  const [form, setForm] = useState({
    project_id: travaux?.project_id || '',
    property_id: travaux?.property_id || '',
    postes: travaux?.postes || CATEGORIES.map(c => ({
      categorie: c, artisan_id: '', artisan_name: '', budget_prevu: 0, budget_reel: 0,
      depenses: [], taches: [], avancement_pct: 0
    })),
    date_debut: travaux?.date_debut || '',
    date_fin_prevue: travaux?.date_fin_prevue || '',
    date_fin_reelle: travaux?.date_fin_reelle || '',
    notes: travaux?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploadingKey, setUploadingKey] = useState(null); // "posteIdx-depIdx" currently uploading
  const fileInputsRef = useRef({}); // { `${posteIdx}-${depIdx}-${capture?}`: HTMLInputElement }

  // Upload a file (picked from gallery or captured by camera) and attach it to a specific depense
  const uploadFileForDepense = async (posteIdx, depIdx, file) => {
    if (!file) return;
    const key = `${posteIdx}-${depIdx}`;
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // Pre-classify the uploaded file into Documents : folder=factures, linked to project
      const params = new URLSearchParams({ folder: 'factures' });
      if (form.project_id) params.set('project_id', form.project_id);
      const res = await axios.post(`${API}/upload?${params.toString()}`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      // Update the depense with the new file_id + refresh files list so the Select reflects the new entry
      // Use ATOMIC update so both fields land together in the same React commit.
      updateDepenseFields(posteIdx, depIdx, {
        file_id: res.data.id,
        file_name: res.data.filename,
      });
      setFiles(prev => [
        { id: res.data.id, original_filename: res.data.filename, folder: 'factures', project_id: form.project_id || null },
        ...prev.filter(f => f.id !== res.data.id),
      ]);
      toast.success('Facture ajoutée');
    } catch (err) {
      toast.error("Upload échoué");
    } finally {
      setUploadingKey(null);
    }
  };

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        const res = await axios.get(`${API}/files`, { withCredentials: true });
        setFiles(res.data || []);
      } catch { /* ignore */ }
    };
    fetchFiles();
  }, []);

  const updatePoste = (idx, field, value) => {
    setForm(prev => {
      const p = [...prev.postes];
      p[idx] = { ...p[idx], [field]: value };
      if (field === 'artisan_id') {
        const art = artisans.find(a => a.id === value);
        p[idx].artisan_name = art?.name || '';
      }
      return { ...prev, postes: p };
    });
  };

  const addDepense = (idx) => {
    setForm(prev => {
      const p = [...prev.postes];
      p[idx] = {
        ...p[idx],
        depenses: [...p[idx].depenses, { date: new Date().toISOString().slice(0, 10), montant: 0, description: '', file_id: '', file_name: '' }]
      };
      return { ...prev, postes: p };
    });
  };

  const updateDepense = (posteIdx, depIdx, field, value) => {
    setForm(prev => {
      const p = JSON.parse(JSON.stringify(prev.postes));
      p[posteIdx].depenses[depIdx][field] = field === 'montant' ? parseFloat(value) || 0 : value;
      // Auto-calc budget reel
      p[posteIdx].budget_reel = p[posteIdx].depenses.reduce((s, d) => s + (d.montant || 0), 0);
      return { ...prev, postes: p };
    });
  };

  // Atomic multi-field update : used by the facture upload flow so the Select reflects
  // the new file immediately (instead of still showing "Pas de facture").
  const updateDepenseFields = (posteIdx, depIdx, patch) => {
    setForm(prev => {
      const p = JSON.parse(JSON.stringify(prev.postes));
      Object.assign(p[posteIdx].depenses[depIdx], patch);
      p[posteIdx].budget_reel = p[posteIdx].depenses.reduce((s, d) => s + (parseFloat(d.montant) || 0), 0);
      return { ...prev, postes: p };
    });
  };

  const removeDepense = (posteIdx, depIdx) => {
    setForm(prev => {
      const p = JSON.parse(JSON.stringify(prev.postes));
      p[posteIdx].depenses.splice(depIdx, 1);
      p[posteIdx].budget_reel = p[posteIdx].depenses.reduce((s, d) => s + (d.montant || 0), 0);
      return { ...prev, postes: p };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id) { toast.error('Selectionnez un projet'); return; }
    setLoading(true);
    try {
      const proj = projects.find(p => p.id === form.project_id);
      const data = { ...form, property_id: proj?.property_id || form.property_id };
      if (travaux?.id) {
        await axios.put(`${API}/travaux/${travaux.id}`, data, { withCredentials: true });
        toast.success('Suivi mis a jour');
      } else {
        await axios.post(`${API}/travaux`, data, { withCredentials: true });
        toast.success('Suivi cree');
      }
      onSave(); onClose();
    } catch { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const totalPrevu = form.postes.reduce((s, p) => s + (parseFloat(p.budget_prevu) || 0), 0);
  const totalReel = form.postes.reduce((s, p) => s + (parseFloat(p.budget_reel) || 0), 0);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-2">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Projet</Label>
          <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="travaux-project-select"><SelectValue placeholder="Selectionner" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.property_title || 'Projet'} - {p.property_city || ''}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-zinc-300">Date debut</Label>
          <Input type="date" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
        </div>
      </div>

      {/* Summary bar */}
      <div className="flex items-center gap-4 p-3 bg-zinc-800/50 rounded-lg">
        <div className="text-sm"><span className="text-zinc-500">Prevu:</span> <span className="font-mono text-white">{formatCurrency(totalPrevu)}</span></div>
        <div className="text-sm"><span className="text-zinc-500">Reel:</span> <span className={`font-mono ${totalReel > totalPrevu ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(totalReel)}</span></div>
        <div className="text-sm"><span className="text-zinc-500">Ecart:</span> <span className={`font-mono ${totalReel - totalPrevu > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{totalReel - totalPrevu > 0 ? '+' : ''}{formatCurrency(totalReel - totalPrevu)}</span></div>
      </div>

      {/* Postes */}
      <Accordion type="multiple" className="space-y-1">
        {form.postes.map((poste, idx) => {
          const Icon = CAT_ICONS[poste.categorie] || Wrench;
          const ecart = (parseFloat(poste.budget_reel) || 0) - (parseFloat(poste.budget_prevu) || 0);
          return (
            <AccordionItem key={idx} value={`p-${idx}`} className="border border-zinc-800 rounded-lg overflow-hidden">
              <AccordionTrigger className="px-3 py-2 bg-zinc-800/50 hover:bg-zinc-800 hover:no-underline text-sm">
                <div className="flex items-center gap-2 flex-1 mr-3">
                  <Icon className="w-4 h-4 text-emerald-500" />
                  <span className="text-white">{poste.categorie}</span>
                  <div className="ml-auto flex items-center gap-3">
                    {poste.artisan_name && <Badge variant="outline" className="text-[10px] bg-zinc-700 border-zinc-600">{poste.artisan_name}</Badge>}
                    <span className="text-xs font-mono text-zinc-400">{formatCurrency(poste.budget_prevu)}</span>
                    <Progress value={poste.avancement_pct} className="w-16 h-1.5" />
                    <span className="text-xs text-zinc-500">{poste.avancement_pct}%</span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="bg-zinc-900 px-3 py-3 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-xs">Budget prevu</Label>
                    <Input type="number" value={poste.budget_prevu} onChange={(e) => updatePoste(idx, 'budget_prevu', parseFloat(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Artisan</Label>
                    <Select value={poste.artisan_id || 'none'} onValueChange={(v) => updatePoste(idx, 'artisan_id', v === 'none' ? '' : v)}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm"><SelectValue placeholder="Aucun" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun</SelectItem>
                        {artisans.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Avancement %</Label>
                    <Input type="number" min="0" max="100" value={poste.avancement_pct} onChange={(e) => updatePoste(idx, 'avancement_pct', Math.min(100, parseInt(e.target.value) || 0))} className="bg-zinc-950 border-zinc-800 text-white h-8 text-sm" />
                  </div>
                </div>
                {/* Depenses */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-zinc-400 text-xs">Depenses (reel: {formatCurrency(poste.budget_reel)})</Label>
                    <Button type="button" size="sm" variant="outline" className="h-6 text-xs border-zinc-700" onClick={() => addDepense(idx)}>
                      <Plus className="w-3 h-3 mr-1" /> Depense
                    </Button>
                  </div>
                  {poste.depenses.map((dep, dIdx) => {
                    const depKey = `${idx}-${dIdx}`;
                    const isUploading = uploadingKey === depKey;
                    return (
                    <div key={dIdx} className="flex flex-wrap items-center gap-2 mb-1.5">
                      <Input type="date" value={dep.date} onChange={(e) => updateDepense(idx, dIdx, 'date', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs w-28" />
                      <Input type="number" value={dep.montant} onChange={(e) => updateDepense(idx, dIdx, 'montant', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs w-20" placeholder="EUR" />
                      <Input value={dep.description} onChange={(e) => updateDepense(idx, dIdx, 'description', e.target.value)} className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs flex-1 min-w-[100px]" placeholder="Description" />
                      <Select value={dep.file_id || 'none'} onValueChange={(v) => {
                        const file = files.find(f => f.id === v);
                        updateDepense(idx, dIdx, 'file_id', v === 'none' ? '' : v);
                        updateDepense(idx, dIdx, 'file_name', file?.original_filename || '');
                      }}>
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white h-7 text-xs w-36" title={dep.file_name || 'Aucune facture'}>
                          {dep.file_id ? (
                            <span className="truncate text-emerald-400 flex items-center gap-1">
                              <FileIcon className="w-3 h-3 flex-shrink-0" />
                              {dep.file_name || files.find(f => f.id === dep.file_id)?.original_filename || 'Facture liée'}
                            </span>
                          ) : (
                            <SelectValue placeholder="Pas de facture" />
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Pas de facture</SelectItem>
                          {files.map(f => <SelectItem key={f.id} value={f.id}>{f.original_filename}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {/* Direct upload buttons: camera (mobile) + gallery/PDF */}
                      <input
                        ref={(el) => { fileInputsRef.current[`${depKey}-cam`] = el; }}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => { uploadFileForDepense(idx, dIdx, e.target.files?.[0]); e.target.value = ''; }}
                      />
                      <input
                        ref={(el) => { fileInputsRef.current[`${depKey}-file`] = el; }}
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => { uploadFileForDepense(idx, dIdx, e.target.files?.[0]); e.target.value = ''; }}
                      />
                      <Button type="button" size="icon" variant="outline" disabled={isUploading}
                        className="h-7 w-7 flex-shrink-0 border-zinc-700"
                        onClick={() => fileInputsRef.current[`${depKey}-cam`]?.click()}
                        title="Prendre une photo de la facture"
                        data-testid={`camera-facture-${idx}-${dIdx}`}
                      >
                        {isUploading ? <Loader2 className="w-3 h-3 animate-spin text-emerald-400" /> : <Camera className="w-3 h-3 text-emerald-400" />}
                      </Button>
                      <Button type="button" size="icon" variant="outline" disabled={isUploading}
                        className="h-7 w-7 flex-shrink-0 border-zinc-700"
                        onClick={() => fileInputsRef.current[`${depKey}-file`]?.click()}
                        title="Choisir un fichier (PDF / image)"
                        data-testid={`upload-facture-${idx}-${dIdx}`}
                      >
                        <Upload className="w-3 h-3 text-blue-400" />
                      </Button>
                      <Button type="button" size="icon" variant="ghost" className="h-7 w-7 flex-shrink-0" onClick={() => removeDepense(idx, dIdx)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                      {dep.file_id && dep.file_name && (
                        <a href={`${API}/files/${dep.file_id}`} target="_blank" rel="noreferrer"
                          className="text-[10px] text-emerald-400 hover:underline inline-flex items-center gap-1 basis-full sm:basis-auto">
                          <FileIcon className="w-3 h-3" /> {dep.file_name}
                        </a>
                      )}
                    </div>
                    );
                  })}
                </div>
                {ecart !== 0 && (
                  <div className={`text-xs font-mono ${ecart > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                    Ecart: {ecart > 0 ? '+' : ''}{formatCurrency(ecart)}
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <div>
        <Label className="text-zinc-300">Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[60px]" />
      </div>

      <div className="flex justify-end gap-3 pt-2 sticky bottom-0 bg-zinc-900 pb-2">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="save-travaux-button">
          {travaux?.id ? 'Modifier' : 'Creer'}
        </Button>
      </div>
    </form>
  );
};

export const TravauxPage = () => {
  const [travauxList, setTravauxList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [artisans, setArtisans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchData = async () => {
    try {
      const [tRes, pRes, aRes] = await Promise.all([
        axios.get(`${API}/travaux`, { withCredentials: true }),
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/artisans`, { withCredentials: true })
      ]);
      setTravauxList(tRes.data);
      setProjects(pRes.data);
      setArtisans(aRes.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const handleRefresh = async () => {
    setLoading(true);
    // Sync budgets from visit reports for all travaux
    try {
      const tRes = await axios.get(`${API}/travaux`, { withCredentials: true });
      for (const t of tRes.data) {
        await axios.post(`${API}/travaux/${t.id}/sync-budgets`, {}, { withCredentials: true });
      }
    } catch { /* ignore */ }
    await fetchData();
    toast.success('Donnees actualisees depuis les visites techniques');
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/travaux/${id}`, { withCredentials: true });
      toast.success('Suivi supprime');
      fetchData();
    } catch { toast.error('Erreur'); }
  };

  const handleExportPdf = async (id) => {
    try {
      const res = await axios.get(`${API}/travaux/${id}/pdf`, { withCredentials: true, responseType: 'blob' });
      downloadBlob(res.data, `suivi_travaux_${id.slice(0, 8)}.pdf`, 'application/pdf');
      toast.success('PDF téléchargé');
    } catch { toast.error('Erreur PDF'); }
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="travaux-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Suivi des travaux</h1>
          <p className="text-zinc-400 mt-1">Budget previsionnel vs reel, avancement par artisan</p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRefresh} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Button className="bg-white text-black hover:bg-zinc-200" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="add-travaux-button">
            <Plus className="w-4 h-4 mr-2" /> Nouveau suivi
          </Button>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto w-[95vw]">
          <DialogHeader><DialogTitle className="text-white">
            {editing ? 'Modifier le suivi' : 'Nouveau suivi travaux'}
          </DialogTitle></DialogHeader>
          <TravauxForm travaux={editing} projects={projects} artisans={artisans} onSave={fetchData} onClose={() => { setDialogOpen(false); setEditing(null); }} />
        </DialogContent>
      </Dialog>

      {travauxList.length > 0 ? (
        <div className="space-y-4">
          {travauxList.map(t => {
            const ecart = (t.budget_reel_total || 0) - (t.budget_prevu_total || 0);
            return (
              <Card key={t.id} className="bg-zinc-900 border-zinc-800 card-hover">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-white">{t.property_title || 'Projet'}</h3>
                      <p className="text-sm text-zinc-500">{t.property_city}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="text-xs text-zinc-500">Avancement global</p>
                        <p className="text-lg font-bold font-mono text-white">{t.avancement_global || 0}%</p>
                      </div>
                      <Progress value={t.avancement_global || 0} className="w-24 h-2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <p className="text-xs text-zinc-500">Budget prevu</p>
                      <p className="font-mono text-white">{formatCurrency(t.budget_prevu_total)}</p>
                    </div>
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <p className="text-xs text-zinc-500">Budget reel</p>
                      <p className={`font-mono ${ecart > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{formatCurrency(t.budget_reel_total)}</p>
                    </div>
                    <div className="p-2 bg-zinc-800/50 rounded text-center">
                      <p className="text-xs text-zinc-500">Ecart</p>
                      <p className={`font-mono flex items-center justify-center gap-1 ${ecart > 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                        {ecart > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {ecart > 0 ? '+' : ''}{formatCurrency(ecart)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => { setEditing(t); setDialogOpen(true); }}>
                      <Wrench className="w-4 h-4 mr-1" /> Modifier
                    </Button>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-300" onClick={() => handleExportPdf(t.id)}>
                      <Download className="w-4 h-4 mr-1" /> PDF
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(t.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="py-16 text-center">
            <Wrench className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">Aucun suivi de travaux</h3>
            <p className="text-zinc-500">Creez un suivi pour suivre vos depenses par corps de metier</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
